import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

/**
 * Reusable Table Component
 * 
 * Professional data table with sorting, pagination, and action buttons.
 * Designed for displaying and managing various types of data in the admin panel.
 */
const Table = ({
  columns = [],
  data = [],
  loading = false,
  pagination = null,
  onPageChange = null,
  onSort = null,
  sortConfig = null,
  emptyMessage = "No data available",
  className = "",
}) => {
  const handleSort = (column) => {
    if (column.sortable && onSort) {
      const direction = 
        sortConfig?.key === column.key && sortConfig?.direction === 'asc' 
          ? 'desc' 
          : 'asc';
      onSort(column.key, direction);
    }
  };

  const getSortIcon = (column) => {
    if (!column.sortable) return null;
    
    if (sortConfig?.key === column.key) {
      return (
        <span className="ml-1">
          {sortConfig.direction === 'asc' ? '↑' : '↓'}
        </span>
      );
    }
    
    return <span className="ml-1 text-secondary-300">↕</span>;
  };

  const renderCell = (item, column) => {
    if (column.render) {
      return column.render(item);
    }
    
    const value = column.key.split('.').reduce((obj, key) => obj?.[key], item);
    return value || '-';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="animate-pulse">
          <div className="h-12 bg-secondary-100 rounded-t-lg"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-secondary-50 border-t border-secondary-100"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Table Container with proper overflow handling */}
      <div className="overflow-x-auto w-full">
        <table className="min-w-full divide-y divide-secondary-200">
          <thead className="table-header sticky top-0 bg-white z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`table-cell font-semibold ${
                    column.sortable ? 'cursor-pointer hover:bg-secondary-100' : ''
                  } ${column.className || ''}`}
                  onClick={() => handleSort(column)}
                  style={{ width: column.width }}
                >
                  <div className="flex items-center">
                    {column.title}
                    {getSortIcon(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-secondary-200">
            {data.length > 0 ? (
              data.map((item, index) => (
                <tr
                  key={item.id || index}
                  className="hover:bg-secondary-50 transition-colors duration-150"
                >
                  {columns.map((column) => (
                    <td key={column.key} className={`table-cell ${column.className || ''}`}>
                      {renderCell(item, column)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="table-cell text-center py-12 text-secondary-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Fixed Pagination at bottom */}
      {pagination && pagination.totalPages > 0 && (
        <div className="flex-shrink-0 flex flex-col md:flex-row items-center justify-between px-4 md:px-6 py-4 bg-secondary-50 border-t border-secondary-200 space-y-2 md:space-y-0">
          <div className="text-sm text-secondary-700 text-center md:text-left">
            Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
          </div>

          <div className="flex items-center space-x-2 overflow-x-auto">
            <button
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            {/* Page numbers */}
            <div className="flex space-x-1 overflow-x-auto max-w-48">
              {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                const startPage = Math.max(1, pagination.currentPage - 2);
                const page = startPage + i;
                if (page > pagination.totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-2 md:px-3 py-1 text-sm rounded flex-shrink-0 ${
                      page === pagination.currentPage
                        ? 'bg-primary-600 text-white'
                        : 'text-secondary-700 hover:bg-secondary-100'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Action Button Component for table cells
 */
export const ActionButton = ({ 
  onClick, 
  icon: Icon, 
  label, 
  variant = 'secondary',
  size = 'sm',
  disabled = false,
  className = ""
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`
        btn btn-${variant} btn-${size} 
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      <Icon className="w-4 h-4" />
      <span className="sr-only">{label}</span>
    </button>
  );
};

/**
 * Dropdown Action Menu Component for table cells
 */
export const ActionMenu = ({ actions = [], item }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState('bottom');
  const menuRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position to prevent cutoff
  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = actions.length * 40 + 16; // Approximate height

      // Check if dropdown would overflow below viewport
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="btn btn-secondary btn-sm"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 z-50 w-48 bg-white rounded-lg shadow-lg border border-secondary-200 ${
            dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
          style={{
            position: 'fixed',
            right: buttonRef.current ? `${window.innerWidth - buttonRef.current.getBoundingClientRect().right}px` : '0px',
            [dropdownPosition === 'top' ? 'bottom' : 'top']: buttonRef.current ?
              (dropdownPosition === 'top' ?
                `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px` :
                `${buttonRef.current.getBoundingClientRect().bottom + 8}px`
              ) : '0px'
          }}
        >
          <div className="py-1">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  onClick={() => {
                    action.onClick(item);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full text-left px-4 py-2 text-sm flex items-center hover:bg-secondary-50
                    ${action.variant === 'danger' ? 'text-red-600 hover:text-red-700' : 'text-secondary-700'}
                  `}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Status Badge Component for table cells
 */
export const StatusBadge = ({ status, variant = 'default' }) => {
  const variants = {
    default: 'bg-secondary-100 text-secondary-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
    primary: 'bg-primary-100 text-primary-800',
  };

  return (
    <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-xs font-medium rounded-full ${variants[variant]} max-w-full truncate`}>
      <span className="truncate">{status}</span>
    </span>
  );
};

export default Table;